import React, { useState } from 'react';
import { X, ArrowLeft, HelpCircle, Clock } from 'lucide-react';

interface Teacher {
  id: string;
  name: string;
  avatar?: string;
}

interface CourseFormData {
  name: string;
  cohort: string;
  startDate: string;
  numberOfMeetings: number;
  teachers: Teacher[];
  price: number;
  fieldOfStudy: string;
  isOnlineOnly: boolean;
  days: string[];
  startTime: string;
  endTime: string;
  customHours: { [key: string]: { enabled: boolean; startTime: string; endTime: string } };
}

interface CourseFormModalProps {
  course?: any;
  onClose: () => void;
  onSave: (data: Partial<CourseFormData>) => void;
}

const availableTeachers: Teacher[] = [
  { id: '1', name: 'Sarah Cohen' },
  { id: '2', name: 'David Levi' },
  { id: '3', name: 'Rachel Green' },
  { id: '4', name: 'Michael Brown' },
  { id: '5', name: 'Yael Shapiro' },
];

const fieldsOfStudy = [
  'Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'English',
  'Hebrew',
  'History',
  'Geography',
  'Computer Science',
  'Art',
  'Music',
  'Physical Education',
];

const weekdays = [
  { id: 'sun', label: 'Sun', labelHe: 'א' },
  { id: 'mon', label: 'Mon', labelHe: 'ב' },
  { id: 'tue', label: 'Tue', labelHe: 'ג' },
  { id: 'wed', label: 'Wed', labelHe: 'ד' },
  { id: 'thu', label: 'Thu', labelHe: 'ה' },
  { id: 'fri', label: 'Fri', labelHe: 'ו' },
  { id: 'sat', label: 'Sat', labelHe: 'ש' },
];

export function CourseFormModal({ course, onClose, onSave }: CourseFormModalProps) {
  const [formData, setFormData] = useState<CourseFormData>({
    name: course?.name || '',
    cohort: course?.cohort || '',
    startDate: course?.startDate || '',
    numberOfMeetings: course?.meetingsCount || 10,
    teachers: course?.teachers || [],
    price: course?.price || 0,
    fieldOfStudy: course?.fieldOfStudy || '',
    isOnlineOnly: course?.isOnlineOnly || false,
    days: course?.days || [],
    startTime: course?.startTime || '09:00',
    endTime: course?.endTime || '10:30',
    customHours: {},
  });

  const [showCustomHoursModal, setShowCustomHoursModal] = useState(false);
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');

  const isEditMode = !!course;

  const handleDayToggle = (dayId: string) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(dayId)
        ? prev.days.filter((d) => d !== dayId)
        : [...prev.days, dayId],
    }));
  };

  const handleTeacherSelect = (teacher: Teacher) => {
    if (!formData.teachers.find((t) => t.id === teacher.id)) {
      setFormData((prev) => ({
        ...prev,
        teachers: [...prev.teachers, teacher],
      }));
    }
    setShowTeacherDropdown(false);
    setTeacherSearch('');
  };

  const handleTeacherRemove = (teacherId: string) => {
    setFormData((prev) => ({
      ...prev,
      teachers: prev.teachers.filter((t) => t.id !== teacherId),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleReset = () => {
    setFormData({
      name: '',
      cohort: '',
      startDate: '',
      numberOfMeetings: 10,
      teachers: [],
      price: 0,
      fieldOfStudy: '',
      isOnlineOnly: false,
      days: [],
      startTime: '09:00',
      endTime: '10:30',
      customHours: {},
    });
  };

  const filteredTeachers = availableTeachers.filter(
    (t) =>
      t.name.toLowerCase().includes(teacherSearch.toLowerCase()) &&
      !formData.teachers.find((ft) => ft.id === t.id)
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isEditMode ? 'Edit Course' : 'Create New Course'}
                </h2>
                {isEditMode && (
                  <p className="text-sm text-gray-500">{course.name}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Online Only Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Online Only Course
                </label>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, isOnlineOnly: !prev.isOnlineOnly }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.isOnlineOnly ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.isOnlineOnly ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              {/* Course Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Type a name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>

              {/* Cohort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cohort
                </label>
                <input
                  type="text"
                  value={formData.cohort}
                  onChange={(e) => setFormData((prev) => ({ ...prev, cohort: e.target.value }))}
                  placeholder="e.g., Year 1 / Cohort A2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Start Date & Number of Meetings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Meetings
                  </label>
                  <select
                    value={formData.numberOfMeetings}
                    onChange={(e) => setFormData((prev) => ({ ...prev, numberOfMeetings: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {[...Array(30)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Add Teachers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Teachers
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={teacherSearch}
                    onChange={(e) => {
                      setTeacherSearch(e.target.value);
                      setShowTeacherDropdown(true);
                    }}
                    onFocus={() => setShowTeacherDropdown(true)}
                    placeholder="Choose or search for a teacher..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {showTeacherDropdown && filteredTeachers.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                      {filteredTeachers.map((teacher) => (
                        <button
                          key={teacher.id}
                          type="button"
                          onClick={() => handleTeacherSelect(teacher)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
                            {teacher.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span>{teacher.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Selected Teachers */}
                {formData.teachers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.teachers.map((teacher) => (
                      <span
                        key={teacher.id}
                        className="flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                      >
                        {teacher.name}
                        <button
                          type="button"
                          onClick={() => handleTeacherRemove(teacher.id)}
                          className="hover:text-teal-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Price */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Price
                  </label>
                  <button type="button" className="text-gray-400 hover:text-gray-600">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Field of Study */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field of Study
                </label>
                <select
                  value={formData.fieldOfStudy}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fieldOfStudy: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select a field...</option>
                  {fieldsOfStudy.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>

              {/* Days Occurring */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days Occurring
                </label>
                <div className="flex flex-wrap gap-2">
                  {weekdays.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => handleDayToggle(day.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.days.includes(day.id)
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Course Hours */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Course Hours
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCustomHoursModal(true)}
                    className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                  >
                    <Clock className="w-4 h-4" />
                    Custom hours
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start</label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Reset
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
              >
                {isEditMode ? 'Save Changes' : 'Create Course'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Custom Hours Modal */}
      {showCustomHoursModal && (
        <CustomHoursModal
          courseName={formData.name}
          customHours={formData.customHours}
          onClose={() => setShowCustomHoursModal(false)}
          onSave={(hours) => {
            setFormData((prev) => ({ ...prev, customHours: hours }));
            setShowCustomHoursModal(false);
          }}
        />
      )}
    </div>
  );
}

interface CustomHoursModalProps {
  courseName: string;
  customHours: { [key: string]: { enabled: boolean; startTime: string; endTime: string } };
  onClose: () => void;
  onSave: (hours: { [key: string]: { enabled: boolean; startTime: string; endTime: string } }) => void;
}

function CustomHoursModal({ courseName, customHours, onClose, onSave }: CustomHoursModalProps) {
  const days = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' },
    { id: 'saturday', label: 'Saturday' },
    { id: 'sunday', label: 'Sunday' },
  ];

  const [hours, setHours] = useState<{ [key: string]: { enabled: boolean; startTime: string; endTime: string } }>(
    days.reduce((acc, day) => ({
      ...acc,
      [day.id]: customHours[day.id] || {
        enabled: !['saturday', 'sunday'].includes(day.id),
        startTime: '09:00',
        endTime: '09:00',
      },
    }), {})
  );

  const handleToggleDay = (dayId: string) => {
    setHours((prev) => ({
      ...prev,
      [dayId]: { ...prev[dayId], enabled: !prev[dayId].enabled },
    }));
  };

  const handleTimeChange = (dayId: string, field: 'startTime' | 'endTime', value: string) => {
    setHours((prev) => ({
      ...prev,
      [dayId]: { ...prev[dayId], [field]: value },
    }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Custom Hours</h3>
          {courseName && (
            <p className="text-sm text-gray-500">{courseName}</p>
          )}
        </div>

        <div className="p-6 space-y-4">
          {days.map((day) => (
            <div key={day.id} className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => handleToggleDay(day.id)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  hours[day.id].enabled
                    ? 'bg-teal-600 border-teal-600'
                    : 'border-gray-300'
                }`}
              >
                {hours[day.id].enabled && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <span className="w-24 text-sm text-gray-700">{day.label}</span>
              {hours[day.id].enabled ? (
                <>
                  <input
                    type="time"
                    value={hours[day.id].startTime}
                    onChange={(e) => handleTimeChange(day.id, 'startTime', e.target.value)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="time"
                    value={hours[day.id].endTime}
                    onChange={(e) => handleTimeChange(day.id, 'endTime', e.target.value)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </>
              ) : (
                <span className="text-sm text-gray-400">Closed</span>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            You can always change these options
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => onSave(hours)}
            className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
